import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { isPolledQuery, markRateLimited } from '@/lib/api/backoff';
import { ApiError, setAccessChangedHandler } from '@/lib/api/client';
import { announceFetch, attachTabSync } from '@/lib/api/tab-sync';

/**
 * A 429 with no `Retry-After` (a proxy stripped it, or CORS hid it — see the exposedHeaders
 * comment in `lumen-be/src/app.ts`) still has to pause something. Half the per-user window.
 */
const DEFAULT_BACKOFF_MS = 30_000;

/**
 * One QueryClient per app instance, created in state so it survives re-renders but is not
 * a module singleton — a module-level client leaks cache between tests.
 */
export function Providers({ children }: { children: ReactNode }): ReactNode {
  const [client] = useState(
    () =>
      new QueryClient({
        /*
         * A 429 from ANY query — not just a polling one — means the whole client is over
         * budget right now. This is the one place that is true regardless of which hook hit
         * it, which is why `markRateLimited` lives here rather than in each poller's own
         * `retry`: a per-hook `onError` only knows about itself, and the whole point is that
         * every OTHER poller needs to hear about this too. See `lib/api/backoff.ts`.
         */
        queryCache: new QueryCache({
          onError: (error) => {
            if (error instanceof ApiError && error.status === 429) {
              markRateLimited(error.retryAfterMs ?? DEFAULT_BACKOFF_MS);
            }
          },
          /*
           * Only an interval-polled query's success is worth telling other tabs about — a
           * one-shot fetch (a report, a form submit's refetch) has no "other tab's timer" to
           * save a round trip for, so broadcasting it would just cost a postMessage and a
           * structured clone for nothing. `isPolledQuery` is `backoff.ts`'s own record of
           * which keys a `pollInterval` has actually been asked about.
           */
          onSuccess: (data, query) => {
            if (isPolledQuery(query.queryKey)) announceFetch(query.queryKey, data);
          },
        }),
        defaultOptions: {
          queries: {
            // The measured reporting cadence is 180s, so aggressive refetching buys
            // nothing. Individual hooks opt into polling where it matters.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: (count, error) => {
              if (error instanceof ApiError && error.isClientFault) return false;
              return count < 2;
            },
            /*
             * A 429's retry waits for the window the SERVER named, not a blind exponential
             * guess — the guess could easily be shorter than what actually clears the limit,
             * which is retrying into the same wall. Every other error keeps the library's
             * ordinary backoff.
             */
            retryDelay: (attemptIndex, error) =>
              error instanceof ApiError && error.retryAfterMs !== null
                ? error.retryAfterMs
                : Math.min(1000 * 2 ** attemptIndex, 30_000),
          },
        },
      }),
  );

  /*
   * ─────────────────────────────────────────────────────────────────────────────
   * WHEN THE SERVER SAYS THIS CALLER'S AUTHORITY MOVED, THROW THE CACHE AWAY
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * `lib/api/client.ts` raises this from two signals — a changed `x-lumen-access` digest on any
   * response, and a 403 — both of which mean the same thing: what this person may see is no
   * longer what the cache holds. See the long note there for why it is neither polling nor a
   * push channel.
   *
   * ── EVERYTHING, NOT JUST `['me']` ────────────────────────────────────────────
   * Refetching identity alone would fix the navigation and leave the LISTS wrong, which is the
   * more misleading half: a person narrowed to one plant would keep the other four plants'
   * devices, series and alarms on screen, drawn from a cache the server would no longer serve.
   * Access narrows reads everywhere, so everything derived from a read is suspect.
   *
   * `refetchType: 'active'` so only MOUNTED queries refetch now; the rest are marked stale and
   * refetch if and when something mounts them. Invalidating the whole cache eagerly would fire
   * every query the session has ever run.
   */
  useEffect(() => {
    setAccessChangedHandler(() => {
      void client.invalidateQueries({ refetchType: 'active' });
    });
    return () => setAccessChangedHandler(() => {});
  }, [client]);

  /* Cross-tab poll collapsing — see `lib/api/tab-sync.ts`. */
  useEffect(() => attachTabSync(client), [client]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
